import { useCallback, useMemo } from "react";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";

/** Board member as the UI needs it (`id` is the board-user id, `userId` the account id). */
export type BoardUserSummary = {
  id: number;
  userId?: number;
  name?: string;
  email?: string;
  profileImageURL?: string;
  archived?: boolean;
  isDeactivated?: boolean;
};

type BoardUsersSource = {
  find?: (predicate: (user: BoardUserSummary) => boolean) => BoardUserSummary | undefined;
  getById?: (id: number | string) => BoardUserSummary | undefined;
  forEach?: (callback: (user: BoardUserSummary) => void) => void;
};

/**
 * Board members of the selected board. Lookups run at call time, and the returned functions change
 * whenever a member is added, updated or removed, so memos and effects that use them refresh.
 * The only place checklist code reads the project's board-users store.
 */
export function useBoardUsers() {
  const { project, user } = useSwivuBryntumContext();
  const source = project?.boardUsersStore as unknown as BoardUsersSource | undefined;
  const { version } = useSwivuStore({ storeId: STORE_KEYS.boardUserStore });

  const byId = useCallback((id?: number | null) => (id == null ? undefined : source?.getById?.(id)), [source, version]);
  const byUserId = useCallback(
    (userId?: number | null) => (userId == null ? undefined : source?.find?.((member) => member.userId === userId)),
    [source, version]
  );
  const all = useCallback(() => {
    const members: BoardUserSummary[] = [];
    source?.forEach?.((member) => members.push(member));
    return members;
  }, [source, version]);
  const nameOf = useCallback((id?: number | null) => byId(id)?.name, [byId]);
  /** Board-user id of the signed-in user, or undefined when they are not on this board. */
  const myBoardUserId = useCallback(() => byUserId(user?.id)?.id, [byUserId, user?.id]);

  return useMemo(() => ({ byId, byUserId, all, nameOf, myBoardUserId }), [byId, byUserId, all, nameOf, myBoardUserId]);
}
